import { FC, useEffect, useRef, useState } from "react"

import { Flex, Space, Typography } from "antd"

import { ColorifiedButton } from "@/components/_Shared/Button/ColorifiedButton"
import { Table } from "@/components/_Shared/Table"
import { BulkOrderAddForm, BulkOrderAddFormProps } from "@/components/BulkOrder/BulkOrderAddForm/BulkOrderAddForm"
import BulkOrderTimer from "@/components/BulkOrder/BulkOrderTimer/BulkOrderTimer"
import RequestQuoteInfo from "@/components/BulkOrder/RequestQuoteInfo/RequestQuoteInfo"
import { Resource, Scope } from "@/constants/permission"
import { useNotificationContext } from "@/context/NotificationContext"
import { useSignalR } from "@/context/SignalRContext"

import { usePersonalOffersFilters } from "./PersonalOffersFilters"

import { useBulkOrderMutation } from "@/hook/BulkOrders/useBulkOrderMutation"
import { usePersonalOfferQuery } from "@/hook/BulkOrders/usePersonalOfferQuery"
import { usePermission } from "@/hook/usePermission"
import { useTable } from "@/hook/useTable"

import { BulkOrderStatus, bulkOrderStatuses, IBulkOrder, PERSONAL_OFFER_KEY, PersonalOfferTabs } from "@/types/IBulkOrder"
import { INotification, NotificationType } from "@/types/INotificaiton"
import { OrderStatusTabKeys } from "@/types/IOrder"
import { TimerType } from "@/types/ITimer"
import { ITableColumn } from "@/types/ui"

import { getInitialColumns } from "@/utils/getInitialColumns"

const { Text } = Typography

const PersonalOffersPage: FC = () => {
    const { hasPermission } = usePermission()
    const [isAdmin, setIsAdmin] = useState<boolean>(hasPermission(Resource.RequestQuotes, Scope.AllActions))
    const { notificationApi } = useNotificationContext()
    const connection = useSignalR()

    const bulkOrderComponent = useRef<BulkOrderAddFormProps>(null)

    const { changeStatusMutation, deleteMutation } = useBulkOrderMutation()

    const mainpersonalOffersColumns: ITableColumn<IBulkOrder>[] = [
      {
        title: "Информация",
        key: "id",
        dataIndex: ["id"],
        sorter: false,
        isVisible: true,
        width: 700,
        order: 1,
        render: (_, bulkOrder)  =>
        <>
            <RequestQuoteInfo
                bulkOrder={bulkOrder}
            />
        </>
      },
      {
        title: "Клиент",
        key: "customerName",
        dataIndex: ["customerName"],
        isVisible: isAdmin,
        order: 2
      },
      {
        title: "Предложение действует",
        key: "createdOn",
        dataIndex: ["createdOn"],
        sorter: true,
        isVisible: true,
        order: 3,
        render: (_, { id, status, timerAdminStart, timerAdminDuration, timerCustomerStart, timerCustomerDuration })  =>
        <>
            { status === BulkOrderStatus.CurrentOffer && timerCustomerStart && timerCustomerDuration &&
                <BulkOrderTimer 
                    id={id}
                    type={TimerType.BulkOrderCustomer}
                    status={status}
                    start={timerCustomerStart}
                    duration={timerCustomerDuration}
                />
            }
            { status === BulkOrderStatus.InProcess && timerAdminStart && timerAdminDuration &&
                <BulkOrderTimer 
                    id={id}
                    type={TimerType.BulkOrderAdmin}
                    status={status}
                    start={timerAdminStart}
                    duration={timerAdminDuration}
                />
            }
        </>
      },
      {   
          title: "Статус",
          key: "status",
          dataIndex: ["status"],
          sorter: true,
          isVisible: true,
          order: 4,
          render: (_, { status, id })  =>
          <>
                  <Flex justify="center">
                  <Text>{bulkOrderStatuses.find(x => x.type === status)?.name}</Text>
              </Flex>
          </>
      },
      {
          title: "Действие",
          key: "action",
          dataIndex: ["action"],
          sorter: true,
          isVisible: true,
          order: 5,
          render: (_, { status, id })  =>
          <>
              <Flex>
                  <Space>
                      {isAdmin ? 
                      <>
                          <ColorifiedButton
                          themecolor="blue"       
                          type={"primary"}
                          size={"small"}
                          disabled={status !== BulkOrderStatus.CurrentOffer}
                          onClick={()=> bulkOrderComponent.current?.open(id, true)}
                          >
                            Редактировать
                          </ColorifiedButton>
                          <ColorifiedButton
                          themecolor="orange"       
                          type={"primary"}
                          size={"small"}
                          disabled={status !== BulkOrderStatus.CurrentOffer}
                          onClick={ async () => {  
                              await deleteMutation.mutateAsync({ ids: [id] })
                              await refetch()
                            }
                          }
                          >
                            Удалить
                          </ColorifiedButton>
                      </>
                      :
                      <>
                          <ColorifiedButton
                          themecolor="green"       
                          type={"primary"}
                          size={"small"}
                          disabled={status !== BulkOrderStatus.CurrentOffer}
                          onClick={async () => {
                              await changeStatusMutation.mutateAsync({ bulkOrderId: id, status: BulkOrderStatus.Accept })
                              await refetch()
                            }}
                          >
                            Принять
                          </ColorifiedButton>
                          <ColorifiedButton
                            onClick={async () => {
                                await changeStatusMutation.mutateAsync({ bulkOrderId: id, status: BulkOrderStatus.Canceled })
                                await refetch()
                            }}
                            themecolor="orange"       
                            type={"primary"}
                            size={"small"}
                            disabled={status !== BulkOrderStatus.CurrentOffer}
                            >
                                Отменить
                            </ColorifiedButton>
                        </>
                      }
                  </Space>
              </Flex>
          </>   
      }
    ]

    const { columns, pagination, filter, order } = useTable({
        initialColumns: getInitialColumns(mainpersonalOffersColumns, PERSONAL_OFFER_KEY),
        key: PERSONAL_OFFER_KEY
    })

    const { filters } = usePersonalOffersFilters()

    const { data, isLoading, refetch } = usePersonalOfferQuery(
        {
            Page: pagination.page,
            PageSize: pagination.pageSize,
            Filter: filter.filter,
            OrderBy: order.orderBy
        }
      )

    useEffect(()=>{
            connection?.on("ReciveNotification", (notificaiton: INotification) => {
                if(notificaiton.notificationType === NotificationType.BulkOrder){
                    refetch()
                }
            })
        },[connection, notificationApi])

    const onBulkOrderHandler = () => {
        bulkOrderComponent.current?.open(undefined, true)
    }
        
    return (
      <> 
        <BulkOrderAddForm ref={bulkOrderComponent}/>
        <Table
            tableTitle="Персональные предложения"
            dataSource={data?.response.data}
            columns={columns.data}
            rowKey="id"
            isLoading={isLoading}
            isSelectable={isAdmin ?  true : false}
            pagination={{
                currentPage: pagination.page,
                pageSize: pagination.pageSize,
                total: data?.response.count,
                onPageChange: pagination.onPageChange,
                onPageSizeChange: pagination.onPageSizeChange
            }}
            filters={{
              filters: filters,
              onSubmit: filter.onFilterChange,
              onCancel: filter.onFilterReset,
              filterKey: PERSONAL_OFFER_KEY
            }}
            showHeader={false}
            onSorterChange={order.setOrderBy}
            addText={isAdmin ? "Добавить" : undefined}
            onAdd={isAdmin ? onBulkOrderHandler : undefined}
            permission={{
                resource: Resource.RequestQuotes
            }}
            statusTabs={{
                defaultActiveKey: OrderStatusTabKeys.All,
                items: PersonalOfferTabs
            }}
        />
      </>
    )
}

export default PersonalOffersPage